import { useState , useEffect,useRef  } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Lock, User, Eye, EyeOff, CheckCircle, X ,FileText,CheckCircle2,efreshCw, AlertCircle} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { useFormik } from 'formik';
import axios from 'axios';
import * as Yup from 'yup';
import { BASE_URL } from '../lib/Api';
import { Alert,AlertDescription } from '../components/ui/alert';
export default function SignUpPage({ onLoginSuccess, onNavigateToLogin }) {
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isOAuthLoading, setIsOAuthLoading] = useState(false);
  // State 2: Business Plan Modal (New)
  const [showPlanModal, setShowPlanModal] = useState(false);
  // State 3: Annual Pricing Toggle (Default: Annual for 'Save 20%')
  const [isAnnual, setIsAnnual] = useState(true);
const [isCheckingVerification, setIsCheckingVerification] = useState(false);
const [userEmail, setUserEmail] = useState('');
  const [showRememberModal, setShowRememberModal] = useState(false);
  const [pendingUserData, setPendingUserData] = useState(null);
  const [userPassword, setUserPassword] = useState('');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [canAcceptTerms, setCanAcceptTerms] = useState(false);
  const termsContentRef = useRef(null);

// Add this near the top of your component, after the state declarations
useEffect(() => {
  // Check for verification status in URL
  const urlParams = new URLSearchParams(window.location.search);
  const verification = urlParams.get('verification');
  const email = urlParams.get('email');

  if (verification === 'success' && email) {
    // Email was verified successfully
    setUserEmail(email);
    setShowSuccessModal(true);
    
    // Show success toast
    toast({
      title: 'Email Verified!',
      description: 'Your email has been verified successfully. Checking your account...',
    });

    // Automatically check verification status
    setTimeout(() => {
      checkEmailVerification();
    }, 1000);

    // Clean up URL
    window.history.replaceState({}, document.title, '/signup');
  } else if (verification === 'invalid-token') {
    toast({
      title: 'Invalid Link',
      description: 'The verification link is invalid or has expired.',
      variant: 'destructive',
    });
  } else if (verification === 'already-confirmed') {
    toast({
      title: 'Already Verified',
      description: 'Your email has already been verified. Please login.',
    });
    setTimeout(() => {
      window.location.href = '/';
    }, 2000);
  } else if (verification === 'error') {
    toast({
      title: 'Verification Failed',
      description: 'Something went wrong during verification. Please try again.',
      variant: 'destructive',
    });
  }
}, []); // Empty dependency array - run once on mount

// Capture plan selection from landing page URL params
useEffect(() => {
  const urlParams = new URLSearchParams(window.location.search);
  const plan = urlParams.get('plan');
  const billing = urlParams.get('billing');
  const trial = urlParams.get('trial');

  if (plan) {
    localStorage.setItem('signup_selected_plan', plan);
  }
  if (billing) {
    localStorage.setItem('signup_billing_cycle', billing.toUpperCase());
  }
  if (trial === 'true') {
    localStorage.setItem('signup_start_trial', 'true');
  }
}, []);

  // --- PRICING DATA (Aapka Data) ---
    const pricingPlans = [
    {
      name: "Solopreneur",
      price: "199",
      annualPrice: "159",
      annualTotal: "1,908",
      description: "For freelancers and individual professionals",
      monthlyAvailable: false, // Annual billing only
      features: [
        "Unlimited invoices & customers",
        "Remove Evolvv branding",
        "Upload logo & brand colors",
        "Invoice numbering rules",
        "Delivery Challan & Purchase Orders",
        "Email invoices from system",
        "GST fields (CGST/SGST/IGST)",
        "PDF exports",
        "T&C templates",
        "Cover page templates",
        "Invoice layout templates",
      ],
      cta: "Get Started",
      popular: true,
      highlight: "Most Popular",
    },
    {
      name: "Professional",
      price: "499",
      annualPrice: "399",
      annualTotal: "4,788",
      description: "For high-earning consultants with retainers",
      monthlyAvailable: false, // Annual billing only
      features: [
        "Everything in Solopreneur, plus:",
        "Online payment integration (UPI/Razorpay/Stripe)",
        "Auto payment status updates",
        "Payment tracking dashboard",
        "Monthly & quarterly sales reports",
        "Customer-wise revenue reports",
        "Invoice export (CSV/PDF)",
        "MSA templates",
        "Editable legal clauses",
        "Retainer invoice support",
        "Priority support",
      ],
      cta: "Get Started",
      popular: false,
    },
    {
      name: "Agency",
      price: "399",
      annualPrice: "333",
      annualTotal: "3,999",
      description: "For agencies, studios, and small teams",
      monthlyAvailable: true, // Monthly billing available
      features: [
        "Everything in Professional, plus:",
        "Multiple workspaces",
        "Team members with role-based access",
        "Shared customer base",
        "Referral module (Admin)",
        "Central branding & numbering control",
        "Advanced analytics",
        "Dedicated account manager",
      ],
      cta: "Get Started",
      popular: false,
    },
  ];
useEffect(() => {
  if (!showSuccessModal || !userEmail) return;

  const checkInterval = setInterval(() => {
    checkEmailVerification();
  }, 5000); // Check every 5 seconds

  return () => clearInterval(checkInterval);
}, [showSuccessModal, userEmail]);

  const formik = useFormik({
    initialValues: {
      fullName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      fullName: Yup.string()
        .required('Full Name is required')
        .min(3, 'Full Name must be at least 3 characters'),
      email: Yup.string()
        .email('Invalid email address')
        .required('Email is required'),
      password: Yup.string()
        .min(12, 'Password must be at least 12 characters')
        .matches(/[a-z]/, 'Must include at least one lowercase letter')
        .matches(/[A-Z]/, 'Must include at least one uppercase letter')
        .matches(/[0-9]/, 'Must include at least one number')
        .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Must include at least one special character')
        .required('Password is required'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords must match')
        .required('Confirm Password is required'),
    }),
    onSubmit: async (values, { setSubmitting, setStatus }) => {
      setStatus(null);
      try {
        const response = await axios.post(`${BASE_URL}/authentication/signup`, {
          fullName: values.fullName,
          email: values.email,
          password: values.password,
        });
         setUserEmail(values.email);
        // Show custom success modal instead of toast
        setShowSuccessModal(true);
         setUserPassword(values.password);
        // Reset form
        formik.resetForm();
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || error.message || 'Signup failed. Please try again.';

        if (errorMessage.includes('User already registered') || errorMessage.includes('User already exists')) {
          toast({
            title: 'User already registered',
            description: 'An account with this email already exists.',
            variant: 'destructive',
          });
        } else {
          toast({
            title: 'Unexpected Error',
            description: `${errorMessage}`,
            variant: 'destructive',
          });  
        }

        setStatus(errorMessage);
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Handle OAuth Signup
  const handleOAuthSignup = async (provider) => {
    setIsOAuthLoading(true);
    try {
      // Redirect to backend OAuth endpoint
      window.location.href = `${BASE_URL}/authentication/oauth/${provider}`;
    } catch (error) {
      console.error('OAuth error:', error);
      toast({
        title: 'OAuth Error',
        description: 'Failed to initiate OAuth signup. Please try again.',
        variant: 'destructive',
      });
      setIsOAuthLoading(false);
    }
  };

  const handleModalClose = () => {
    toast({
    title: 'Email Verification Pending',
    description: 'Please verify your email before proceeding.',
    variant: 'destructive',
  });
  };

  const handlePlanSelect = (planName) => {
    console.log("Selected Plan:", planName);
    // Store the selected plan in localStorage so the pricing page knows which plan to highlight
    localStorage.setItem('selected_plan_from_signup', planName);
    setShowPlanModal(false);
    // Redirect to login page - after login, user will be redirected to pricing/onboarding
    window.location.href = '/';
  };
// Check if email is verified
const checkEmailVerification = async () => {
    setIsCheckingVerification(true);
    
    try {
      const response = await axios.get(`${BASE_URL}/authentication/check-verification`, {
        params: { email: userEmail ,  password: userPassword }
      });
      
      const data = response.data;
      
      if (data.isVerified) {
        // ✅ Store user data temporarily
        setPendingUserData({
          token: data.token,
          user: data.user
        });

        // ✅ Close success modal and show Remember Me modal
        setShowSuccessModal(false);
        setShowRememberModal(true);
        
        toast({
          title: 'Email Verified!',
          description: 'Please choose how long you want to stay logged in.',
        });
      }
    } catch (error) {
      console.error('Verification check error:', error);
    } finally {
      setIsCheckingVerification(false);
    }
  };

  // ✅ NEW: Complete Login with Duration
  const completeLoginWithDuration = (days) => {
    if (!pendingUserData) {
      console.error('[SignUp] No pending user data found');
      return;
    }

    try {
      const expiryAt = Date.now() + days * 24 * 60 * 60 * 1000;
      
      console.log('[SignUp] Setting login duration:', {
        days: days,
        expiryAt: new Date(expiryAt).toISOString()
      });

      // Store authentication data
      localStorage.setItem('supabase_access_token', pendingUserData.token);
      localStorage.setItem('user_id', pendingUserData.user.id);
      localStorage.setItem('user_type', pendingUserData.user.user_type);
      localStorage.setItem('user_email', pendingUserData.user.email);
      localStorage.setItem('user_name', pendingUserData.user.fullName);
      
      // Store login expiry
      localStorage.setItem('login_expiry_at', expiryAt.toString());
      localStorage.setItem('login_duration_days', String(days));

      console.log('[SignUp] User data stored, redirecting to organization...');

      // Close modal
      setShowRememberModal(false);
        setShowTermsModal(true);
      
      // // Show redirect message
      // toast({
      //   title: 'Setup Required',
      //   description: "Let's set up your organization...",
      // });

      // // Redirect to organization page
      // setTimeout(() => {
      //   window.location.href = '/organization';
      // }, 1000);

    } catch (error) {
      console.error('[SignUp] Error in completeLoginWithDuration:', error);
      toast({
        title: 'Error',
        description: 'Failed to complete setup. Please try again.',
        variant: 'destructive',
      });
    }
  };
 const handleTermsScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 5) {
      setCanAcceptTerms(true);
    }
  };

  // ✅ NEW: Accept Button Handler (Same as Login)
  const handleTermsAccept = () => {
    setShowTermsModal(false);
    toast({
      title: 'Welcome, Admin!',
      description: 'Redirecting to your admin panel...',
    });

    // Admin panel pe redirect
    setTimeout(() => {
      if (onLoginSuccess && pendingUserData) {
        onLoginSuccess(pendingUserData.user);
      } else {
        window.location.reload();
      }
    }, 1000);
  };

// const handleManualCheck = () => {
//   checkEmailVerification();
//   toast({
//     title: 'Checking...',
//     description: 'Verifying your email status',
//   });
// };
// // Resend verification email
// const handleResendEmail = async () => {
//   try {
//     await axios.post(`${BASE_URL}/authentication/resend-verification`, {
//       email: userEmail
//     });
    
//     toast({
//       title: 'Email Sent!',
//       description: 'Verification email has been resent. Please check your inbox.',
//     });
//   } catch (error) {
//     console.error('Resend error:', error);
//     toast({
//       title: 'Error',
//       description: 'Failed to resend email. Please try again.',
//       variant: 'destructive',
//     });
//   }
// };
  return (
    <>
      <div className="flex min-h-screen w-full items-center justify-center animated-gradient-background p-4">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex w-full justify-center">
            <img
              src="./images/Evolvv-main.png"
              alt="evolvv Logo"
              width={200}
              height={50}
              className="dark:invert"
            />
          </div>
          <Card className="w-full shadow-xl bg-card/90 backdrop-blur-sm">
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="text-2xl text-card-foreground">Create an Account</CardTitle>
              <CardDescription className="text-card-foreground/80">Enter your details to get started</CardDescription>
            </CardHeader>

            <form onSubmit={formik.handleSubmit}>
              <CardContent className="space-y-4">

                {/* Full Name */}
                <div className="space-y-2">
                  <Label htmlFor="fullName" className="text-card-foreground/90">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="fullName"
                      name="fullName"
                      type="text"
                      placeholder="John Doe"
                      value={formik.values.fullName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="pl-10 bg-background/80 text-foreground placeholder:text-muted-foreground/70"
                    />
                  </div>
                  {formik.touched.fullName && formik.errors.fullName ? (
                    <p className="text-destructive text-sm">{formik.errors.fullName}</p>
                  ) : null}
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-card-foreground/90">Email</Label>
                  <div className="relative" suppressHydrationWarning={true}>
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="name@example.com"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="pl-10 bg-background/80 text-foreground placeholder:text-muted-foreground/70"
                    />
                  </div>
                  {formik.touched.email && formik.errors.email ? (
                    <p className="text-destructive text-sm">{formik.errors.email}</p>
                  ) : null}
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-card-foreground/90">Password</Label>
                  <div className="relative" suppressHydrationWarning={true}>
                    <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="pl-10 pr-10 placeholder:text-muted-foreground/70 bg-background/80 text-foreground"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {formik.touched.password && formik.errors.password ? (
                    <p className="text-destructive text-sm">{formik.errors.password}</p>
                  ) : null}
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-card-foreground/90">Confirm Password</Label>
                  <div className="relative" suppressHydrationWarning={true}>
                    <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formik.values.confirmPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className="pl-10 pr-10 placeholder:text-muted-foreground/70 bg-background/80 text-foreground"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {formik.touched.confirmPassword && formik.errors.confirmPassword ? (
                    <p className="text-destructive text-sm">{formik.errors.confirmPassword}</p>
                  ) : null}
                </div>
              </CardContent>

              <CardFooter className="flex flex-col gap-4">
                <Button type="submit" className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-3 rounded-l transition-all duration-300 transform hover:scale-105" disabled={formik.isSubmitting || isOAuthLoading}>
                  {formik.isSubmitting ? 'Creating Account...' : 'Create Account'}
                </Button>
                                {/* OAuth Buttons */}
{/*            
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => handleOAuthSignup('google')}
                    disabled={isOAuthLoading || formik.isSubmitting}
                  >
                    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                    Sign up with Google
                  </Button> */}

          

                
                <p className="text-center text-sm text-card-foreground/80">
                  Already have an account?{' '}
                  <a href="/" className="font-semibold text-blue-500 hover:text-blue-600 hover:underline transition-colors">
                    Login
                  </a>
                </p>
              </CardFooter>
            </form>
          </Card>
        </div>
      </div>

   
{/* Success Modal with Email Verification */}

      {showSuccessModal && (
        <div className="fixed inset-0 z-20 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-300">
            {/* Header with gradient background */}
            <div className=" p-8 text-center">
          
             <div className=" flex w-full justify-center">
            <img
              src="./images/Evolvv-main.png"
              alt="evolvv Logo"
              width={200}
              height={50}
              className="dark:invert"
            />
          
              </div>
             
            </div>

            {/* Content */}
            <div className="p-8">
              <div className="text-center mb-6">
                <div className="flex justify-center mb-4">
                  <CheckCircle className="w-16 h-16 text-green-500" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">
                  Account Created Successfully!
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Thank you for joining <strong>evolvv</strong>! We're excited to have you on board. 
                  Please check your email for a verification link to activate your account.
                </p>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded-lg mb-6">
                <div className="flex items-start">
                  <Mail className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-blue-900 dark:text-blue-100 text-sm mb-1">
                      Check Your Email
                    </h4>
                    <p className="text-blue-700 dark:text-blue-200 text-xs">
                      A verification email has been sent to your inbox. Please verify your email address to complete the registration.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              {/* <Button 
                onClick={handleModalClose}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-3 rounded-xl transition-all duration-300 transform hover:scale-105"
              >
                OK, Got it!
              </Button> */}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 dark:bg-gray-900 px-8 py-4 text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                You will be redirected to the Organization page
              </p>
            </div>
          </div>
        </div>
      )}

        {/* Remember Me Modal */}
      {showRememberModal && (
        <div className="fixed inset-0 z-30 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-300">
            
            <div className="p-8 text-center">
              <div className="flex w-full justify-center">
                <img
                  src="./images/Evolvv-main.png"
                  alt="evolvv Logo"
                  width={200}
                  height={50}
                  className="dark:invert"
                />
              </div>
            </div>

            <div className="px-8 pb-8">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">
                  Stay signed in?
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  How long should we keep you signed in on this device?
                </p>
              </div>

              <div className="space-y-3 mb-4">
                <Button
                  onClick={() => completeLoginWithDuration(1)}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-3 rounded-xl transition-all duration-300 transform hover:scale-105"
                >
                  Only 1 day (default)
                </Button>

                <Button
                  onClick={() => completeLoginWithDuration(5)}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-3 rounded-xl transition-all duration-300 transform hover:scale-105"
                >
                  5 days
                </Button>

                <Button
                  onClick={() => completeLoginWithDuration(7)}
                  className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-3 rounded-xl transition-all duration-300 transform hover:scale-105"
                >
                  7 days
                </Button>
              </div>

              <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                Not sure?{' '}
                <button
                  type="button"
                  onClick={() => completeLoginWithDuration(1)}
                  className="underline font-medium"
                >
                  Skip for now (1 day)
                </button>
              </p>
            </div>
          </div>
        </div>
      )}
        {/* ✅ TERMS OF USE MODAL - HIGHEST PRIORITY z-40 */}
      {showTermsModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-2xl max-w-2xl w-full flex flex-col h-[85vh] animate-in fade-in zoom-in duration-300 overflow-hidden border border-gray-200 dark:border-gray-800">
            
            {/* Header */}
            <div className="bg-[#0EA5E9] p-6 text-white shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-full">
                  <FileText className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Terms of Use Agreement</h2>
                  <p className="text-blue-100 text-sm">Please read and accept our terms of use before continuing</p>
                </div>
              </div>
            </div>

            {/* Scrollable Content */}
            <div 
              ref={termsContentRef}
              onScroll={handleTermsScroll}
              className="flex-1 overflow-y-auto p-6 space-y-6 text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/50"
            >
              <div className="space-y-4">
                
                <section>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">1. Acceptance of Terms</h3>
                  <p>By accessing and using Evolvv Invoice, you agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use our services.</p>
                </section>

                <section>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">2. Use of Service</h3>
                  <p>Evolvv Invoice provides invoicing, billing, and business management tools. You agree to:</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Use the service for lawful purposes only</li>
                    <li>Provide accurate and complete information</li>
                    <li>Maintain the security of your account credentials</li>
                    <li>Not share your account with unauthorized users</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">3. Data Privacy & Security</h3>
                  <p>We take your privacy seriously. By using Evolvv Invoice:</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Your data is encrypted and stored securely</li>
                    <li>We do not share your data with third parties without consent</li>
                    <li>You retain ownership of all your business data</li>
                    <li>You can export or delete your data at any time</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">4. Billing & Payments</h3>
                  <p>Subscription fees are billed according to your chosen plan. You agree to:</p>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Pay all applicable fees on time</li>
                    <li>Understand that refunds are subject to our Refund Policy</li>
                    <li>Provide valid payment information</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">5. Intellectual Property</h3>
                  <p>All content, features, and functionality of Evolvv Invoice are owned by us and protected by international copyright, trademark, and other intellectual property laws.</p>
                </section>

                <section>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">6. Limitation of Liability</h3>
                  <p>Evolvv Invoice is provided "as is" without warranties of any kind. We are not liable for any indirect, incidental, or consequential damages arising from your use of the service.</p>
                </section>

                <section>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">7. GST Compliance</h3>
                  <p>While Evolvv Invoice provides GST calculation features, you are responsible for ensuring compliance with all applicable tax laws and regulations. We recommend consulting with a tax professional for specific guidance.</p>
                </section>

                <section>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">8. Service Modifications</h3>
                  <p>We reserve the right to modify, suspend, or discontinue any aspect of our service at any time. We will provide reasonable notice for significant changes.</p>
                </section>

                <section>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">9. Termination</h3>
                  <p>We may terminate or suspend your account if you violate these terms. You may cancel your subscription at any time through your account settings.</p>
                </section>

                <section>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2">10. Governing Law</h3>
                  <p>These Terms of Use are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of courts in India.</p>
                </section>
                
                {/* Scroll Indicator */}
                {!canAcceptTerms && (
                  <div className="py-4 text-center text-blue-600 animate-pulse font-medium bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    ↓ Please scroll down to continue ↓
                  </div>
                )}
                
                <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-900/30 dark:border-blue-800">
                  <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <AlertDescription className="text-blue-800 dark:text-blue-300 text-xs">
                    <strong>Important Notice:</strong> By clicking "I Agree" below, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use.
                  </AlertDescription>
                </Alert>

                <div className="text-xs text-gray-400 mt-4 text-center">
                  Last updated: December 26, 2025
                </div>
              </div>
            </div>

            {/* Footer with Button */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shrink-0 flex justify-end items-center gap-4">
              {!canAcceptTerms && <span className="text-xs text-muted-foreground">Scroll to the bottom to enable this button</span>}
              <Button 
                onClick={handleTermsAccept}
                disabled={!canAcceptTerms}
                className={`
                  min-w-[150px] transition-all duration-300
                  ${canAcceptTerms 
                    ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/30' 
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'}
                `}
              >
                {canAcceptTerms && <CheckCircle2 className="mr-2 h-4 w-4" />}
                I Agree
              </Button>
            </div>

          </div>
        </div>
      )}

         {/* ============================================================ */}
      {/* MODAL 2: BUSINESS PLAN MODAL (Lightweight Version)           */}
      {/* ============================================================ */}
      {showPlanModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md overflow-y-auto">
          {/* Main Container */}
          <div className="relative w-full max-w-7xl bg-white rounded-3xl shadow-2xl overflow-hidden my-auto animate-in zoom-in-95 duration-300">
            
            {/* Header Section */}
            <div className="text-center pt-10 pb-6 px-4 bg-gradient-to-b from-gray-50 to-white border-b border-gray-100">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">Simple, Transparent Pricing</h2>
              <p className="text-gray-600 mt-3 text-lg">Choose the plan that's right for your business. Start free, upgrade as you grow.</p>

              {/* Toggle Switch */}
              <div className="mt-6 inline-flex items-center bg-gray-100 rounded-full p-1 border border-gray-200">
                <button
                  onClick={() => setIsAnnual(false)}
                  className={`px-6 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${!isAnnual ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:text-gray-900"}`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setIsAnnual(true)}
                  className={`px-6 py-2 rounded-full text-sm font-semibold transition-all duration-200 flex items-center ${isAnnual ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:text-gray-900"}`}
                >
                  Annual 
                  {/* Manual Badge (No Dependency) */}
                  <span className="ml-2 bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full border border-green-200">Save 20%</span>
                </button>
              </div>
            </div>

            {/* Pricing Grid */}
            <div className="p-6 md:p-10 bg-gray-50/50">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {pricingPlans.map((plan, index) => (
                  // Simple Div instead of Motion
                  <div 
                    key={index} 
                    className="relative flex"
                  >
                    <Card 
                      className={`flex flex-col w-full p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 
                      ${plan.popular 
                        ? "border-2 border-blue-600 shadow-lg scale-100 md:scale-105 z-10" 
                        : "border border-gray-200"
                      }`}
                    >
                      {/* Highlight Badge (Manual) */}
                      {plan.highlight && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <span className="bg-blue-600 text-white px-3 py-1 text-xs uppercase tracking-wide font-bold rounded-full shadow-md">
                            {plan.highlight}
                          </span>
                        </div>
                      )}

                      <div className="mb-4">
                        <h3 className="text-lg font-bold text-gray-900">{plan.name}</h3>
                        <p className="text-gray-500 text-xs mt-1 min-h-[40px] leading-relaxed">{plan.description}</p>
                      </div>

                      <div className="mb-6">
                        {plan.isFree ? (
                          <span className="text-4xl font-bold text-gray-900">What Included</span>
                        ) : (
                          <div className="flex items-baseline">
                            <span className="text-4xl font-bold text-gray-900">
                              ₹{plan.monthlyAvailable === false || isAnnual ? plan.annualPrice : plan.price}
                            </span>
                            <span className="text-gray-500 text-sm ml-1 font-medium">/mo</span>
                          </div>
                        )}
                        {!plan.isFree && (plan.monthlyAvailable === false || isAnnual) && (
                          <p className="text-xs text-green-600 mt-2 font-medium bg-green-50 inline-block px-2 py-1 rounded">
                            Billed ₹{plan.annualTotal} yearly
                          </p>
                        )}
                        {!plan.isFree && plan.monthlyAvailable && !isAnnual && (
                          <p className="text-xs text-gray-500 mt-2 font-medium bg-gray-50 inline-block px-2 py-1 rounded">
                            Billed monthly
                          </p>
                        )}
                      </div>

                      {/* Features List */}
                      <ul className="space-y-3 mb-6 flex-grow">
                        {plan.features.map((feature, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="leading-tight">{feature}</span>
                          </li>
                        ))}
                        {/* Not Included (Faded) */}
                        {plan.notIncluded?.slice(0, 3).map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-gray-400">
                            <X className="w-3 h-3 flex-shrink-0 mt-0.5 opacity-70" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>

                      <Button 
                        onClick={() => handlePlanSelect(plan.name)}
                        className={`w-full py-6 font-semibold text-md rounded-xl transition-colors ${
                          plan.popular 
                            ? "bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-200" 
                            : "bg-white text-gray-900 border-2 border-gray-100 hover:border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {plan.cta}
                      </Button>
                    </Card>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer Note */}
            <div className="text-center pb-8 text-gray-400 text-xs bg-gray-50/50">
              Prices exclude applicable taxes. You can upgrade or downgrade anytime.
            </div>

          </div>
        </div>
      )}
      
    </>
  );
}